/*
 * <vitesse-widget-publish-dialog> — "put this widget on the marketplace".
 *
 * A board carries its widgets by value; none of them is on any shelf until a person says so, here.
 * The dialog asks the two things only a person can decide: what to call it, and who may see it —
 * this organisation or everyone. The widget's identity is its source: a later change to one line
 * of it is a different widget, with its own place on the shelf, so the button that opens this
 * appears only on a tile whose source is on no shelf yet.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { button } from '@vitesse/base-ui/components/base-buttons';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';

export interface PublishRequest {
  title: string;
  visibility: 'org' | 'global';
}

@customElement('vitesse-widget-publish-dialog')
export class VitesseWidgetPublishDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  /** The name the widget has on its tile — what the field starts with. */
  @property({ type: String }) name = '';
  /** Set while the publish is in flight, so the button cannot be pressed twice. */
  @property({ type: Boolean }) busy = false;
  /** The store's refusal, when there is one. */
  @property({ type: String }) error = '';

  @state() private title_ = '';
  @state() private visibility: 'org' | 'global' = 'org';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  protected willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) {
      this.title_ = this.name;
      this.visibility = 'org';
    }
  }

  private close(): void {
    this.dispatchEvent(new CustomEvent('publish-close', { bubbles: true, composed: true }));
  }

  private confirm(): void {
    const title = this.title_.trim();
    if (!title || this.busy) return;
    this.dispatchEvent(
      new CustomEvent<PublishRequest>('publish-confirm', { detail: { title, visibility: this.visibility }, bubbles: true, composed: true }),
    );
  }

  render() {
    if (!this.open) return nothing;
    const scopes = [
      { value: 'org', label: t('dashboards.publishOrg') },
      { value: 'global', label: t('dashboards.publishGlobal') },
    ];
    return html`<vitesse-dialog open .heading=${t('dashboards.publishWidget')} .width=${'480px'} @dialog-close=${() => this.close()}>
      <div class="dialog-form wcfg">
        <div class="form-group">
          <label class="wcfg-label">${t('dashboards.publishName')}</label>
          <vitesse-text-input
            size="sm"
            .value=${this.title_}
            @change=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              this.title_ = e.detail.value;
            }}
          ></vitesse-text-input>
        </div>
        <div class="form-group">
          <label class="wcfg-label">${t('dashboards.publishScope')}</label>
          <vitesse-select
            size="sm"
            .options=${scopes}
            .value=${this.visibility}
            @change=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              this.visibility = e.detail.value === 'global' ? 'global' : 'org';
            }}
          ></vitesse-select>
          <div class="field-help">${t('dashboards.publishHint')}</div>
        </div>
        ${this.error ? html`<div class="widget-install-error">${this.error}</div>` : nothing}
      </div>
      <div slot="footer" class="dialog-actions">
        ${button({ label: t('dashboards.settingsCancel'), size: 'sm', onClick: () => this.close() })}
        ${button({ label: t('dashboards.publishConfirm'), icon: 'layer-arrow-up', color: 'blue', size: 'sm', disabled: this.busy || !this.title_.trim(), onClick: () => this.confirm() })}
      </div>
    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-widget-publish-dialog': VitesseWidgetPublishDialog;
  }
}
